import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { logsApi } from '../services/api';
import { RunLogEntry, PaginatedResponse } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { useApiQuery } from '../hooks/useApiQuery';
import { TableSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';

export default function LogsPage() {
  const [correlationIdFilter, setCorrelationIdFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [scriptKeyFilter, setScriptKeyFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState<RunLogEntry | null>(null);

  const { data, loading, error, refetch } = useApiQuery<PaginatedResponse<RunLogEntry>>(
    () => logsApi.list({
      correlationId: correlationIdFilter || undefined,
      level: levelFilter || undefined,
      scriptKey: scriptKeyFilter || undefined,
      pageSize: 100,
    }),
    [correlationIdFilter, levelFilter, scriptKeyFilter]
  );

  const logs = data?.items || [];

  const getLevelColor = (level: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (level) {
      case 'Info':
        return 'info';
      case 'Warning':
        return 'warning';
      case 'Error':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleLogClick = (log: RunLogEntry) => {
    setSelectedLog(log);
  };

  const handleCloseDialog = () => {
    setSelectedLog(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Logs</Typography>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={refetch} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          title="Logs konnten nicht geladen werden"
          onRetry={refetch}
        />
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Correlation ID (Run ID)"
                value={correlationIdFilter}
                onChange={(e) => setCorrelationIdFilter(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Level"
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">Alle</MenuItem>
                <MenuItem value="Info">Info</MenuItem>
                <MenuItem value="Warning">Warning</MenuItem>
                <MenuItem value="Error">Error</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Script Key"
                value={scriptKeyFilter}
                onChange={(e) => setScriptKeyFilter(e.target.value)}
                size="small"
                placeholder="z.B. 110, 130"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && logs.length === 0 ? (
        <TableSkeleton rows={8} columns={6} />
      ) : (
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Script Key</TableCell>
                <TableCell>Correlation ID</TableCell>
                <TableCell>Message</TableCell>
                <TableCell align="right">Duration (ms)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">Keine Logs gefunden</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow
                    key={log.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleLogClick(log)}
                  >
                    <TableCell>
                      {formatDateSafe(log.timestamp, 'dd.MM.yyyy HH:mm:ss')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.level}
                        color={getLevelColor(log.level)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.scriptKey}</TableCell>
                    <TableCell>{log.correlationId}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {log.message}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {log.durationMs ? log.durationMs.toLocaleString() : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      )}

      <Dialog open={Boolean(selectedLog)} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Timestamp
                  </Typography>
                  <Typography variant="body1">
                    {formatDateSafe(selectedLog.timestamp, 'dd.MM.yyyy HH:mm:ss')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Level
                  </Typography>
                  <Chip label={selectedLog.level} color={getLevelColor(selectedLog.level)} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Script Key
                  </Typography>
                  <Typography variant="body1">{selectedLog.scriptKey}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Correlation ID
                  </Typography>
                  <Typography variant="body1">{selectedLog.correlationId}</Typography>
                </Grid>
                {selectedLog.durationMs && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1">{selectedLog.durationMs} ms</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Blob Path
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {selectedLog.blobPath}
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Message
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {selectedLog.message}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
